import { useMemo, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import { RouteComponentProps } from "react-router-dom";
import { Helmet } from "react-helmet";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import FullWidthImage from "components/full-width-image";
import ErrorMessage from "components/error-message";
import { AmplitudePageName } from "utils/amplitude";
import TableOfContents from "./components/table-of-contents/TableOfContents";
import ShareThisArticle from "./components/share-this-article/ShareThisArticle";
import BlogCTA from "./components/blog-cta/BlogCTA";
import ReadingProgressBar from "./components/reading-progress-bar/ReadingProgressBar";
import useBlog from "./hooks/useBlog";
import AuthorBasicInfo from "./components/author-basic-info/AuthorBasicInfo";
import AuthorFullBio from "./components/author-full-bio/AuthorFullBio";
import RelatedPosts from "./components/related-posts/RelatedPosts";
import Footer from "components/footer/Footer";
import WriteForOrchid from "./components/write-for-orchid/WriteForOrchid";
import SubscribeToNewsletter from "components/subscribe-to-newsletter/SubscribeToNewsletter";
import useFormatDateForBlog from "./hooks/formatDateForBlog";
import getBlogImageUrl, { BlogImageType } from "./utils/getBlogImageUrl";
import { ImageAltText } from "constants/imageAltText";
import useImageDimensions from "hooks/useImageDimensions";
// import SubscribePopup from "./components/subscribe-popup/SubscribePopup";
import styles from "./Blog.module.scss";
import getClasses from "utils/getClasses";
import getBlogPostStructuredData from "./utils/getBlogPostStructuredData";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "blog-post";

const Blog = (
  props: RouteComponentProps<{
    url: string;
  }>,
) => {
  const { isPhonePortrait } = usePhonePortraitContext();
  let url = props.match.params.url;
  const articleBodyRef = useRef<HTMLDivElement>(null);

  const { formatDateForBlog } = useFormatDateForBlog();

  const { orchidPro, blog, enhancedContent, tableOfContents, readTime } =
    useBlog(url, AMPLITUDE_PAGE_NAME);

  const blogImageSocialUrl = useMemo(
    () => (blog ? getBlogImageUrl(blog, BlogImageType.social) : ""),
    [blog],
  );

  const blogImageAltText = useMemo(
    () => (blog ? ImageAltText[blog?.id] ?? blog.title : ""),
    [blog],
  );

  const { width: socialImageWidth, height: socialImageHeight } =
    useImageDimensions(blogImageSocialUrl);

  // // Move to hooks/useInactiveUserPopup.ts
  // const [hasPopupBeenShown, setHasPopupBeenShown] = useState(false);
  // const [, setShowPopup] = useState(false);
  // const POPUP_SCROLL_THRESHOLD = 0.2;

  // const onScroll = useCallback(() => {
  //   if (hasPopupBeenShown) {
  //     return;
  //   }

  //   const { scrollY } = window;
  //   const { scrollHeight } = document.body;

  //   console.log({ scrollY, scrollHeight });

  //   if (scrollY / scrollHeight > POPUP_SCROLL_THRESHOLD) {
  //     setHasPopupBeenShown(true);
  //     setShowPopup(true);
  //     // SHOW BANNER
  //     console.log("Showing the popup");
  //   }
  // }, [hasPopupBeenShown]);
  // // -------

  // useEffect(() => {
  //   window.addEventListener("scroll", onScroll);
  //   return () => {
  //     window.removeEventListener("scroll", onScroll);
  //   };
  // }, [onScroll]);

  const [error, setError] = useState<any>();

  return !blog ? null : (
    <>
      <Helmet>
        <title>{blog.title}</title>
        <meta name="description" content={blog.summary} />

        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.summary} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={blogImageSocialUrl} />
        <meta property="og:image:url" content={blogImageSocialUrl} />
        <meta property="og:image:secure_url" content={blogImageSocialUrl} />
        <meta property="og:image:alt" content={blogImageAltText} />
        <meta property="og:image:width" content={socialImageWidth} />
        <meta property="og:image:height" content={socialImageHeight} />
        <meta property="og:image:type" content="image/png" />

        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.summary} />
        <meta name="twitter:image" content={blogImageSocialUrl} />
        <meta name="twitter:image:src" content={blogImageSocialUrl} />
        <meta name="twitter:image:alt" content={blogImageAltText} />
        <meta name="twitter:card" content="summary_large_image" />

        <meta
          property="article:published_time"
          content={blog.create_datetime}
        />
        <meta property="article:author" content={blog.author_display_name} />

        <script type="application/ld+json">
          {JSON.stringify(getBlogPostStructuredData(blog))}
        </script>
      </Helmet>

      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error.toString()}
        />
      )}

      <ReadingProgressBar articleBodyRef={articleBodyRef} />
      <div
        className={!isPhonePortrait ? styles["blog"] : styles["blog--phone"]}
      >
        <Container
          className={
            !isPhonePortrait
              ? getClasses(styles["blog__content"], styles["bottom-56"])
              : styles["blog__content--phone"]
          }
        >
          {!isPhonePortrait && (
            <div className={styles["blog__content__side-column"]} />
          )}
          <div className={styles["blog__content__main-column"]}>
            <h1
              className={getClasses(
                styles["blog__title"],
                isPhonePortrait && styles["blog__title--phone"],
              )}
            >
              {blog.title}
            </h1>
            <p
              className={getClasses(
                styles["posted-on"],
                isPhonePortrait && styles["posted-on--phone"],
              )}
            >
              <span>{readTime} min read</span>
              <span>•</span>
              <span>Posted on {formatDateForBlog(blog.create_datetime)}</span>
            </p>
            <AuthorBasicInfo
              orchidPro={orchidPro}
              blog={blog}
              amplitudePageName={AMPLITUDE_PAGE_NAME}
            />
          </div>
          {!isPhonePortrait && (
            <div className={styles["blog__content__side-column"]} />
          )}
        </Container>
        <FullWidthImage
          alt={blogImageAltText}
          className={styles["hero-image"]}
          src={getBlogImageUrl(blog, BlogImageType.main)}
          width={"auto"}
          height={isPhonePortrait ? 210 : 720}
        />
        {isPhonePortrait && (
          <TableOfContents
            data={tableOfContents}
            articleBodyRef={articleBodyRef}
            amplitudePageName={AMPLITUDE_PAGE_NAME}
          />
        )}
        <Container
          className={
            !isPhonePortrait
              ? styles["blog__content"]
              : styles["blog__content--phone"]
          }
        >
          {!isPhonePortrait && (
            <div className={styles["blog__content__side-column"]}>
              <div className={styles["blog__content__sticky-column-content"]}>
                <TableOfContents
                  data={tableOfContents}
                  articleBodyRef={articleBodyRef}
                  amplitudePageName={AMPLITUDE_PAGE_NAME}
                />
              </div>
            </div>
          )}
          <article className={styles["blog__content__main-column"]}>
            <div
              className={
                !isPhonePortrait
                  ? styles["blog__body"]
                  : styles["blog__body--phone"]
              }
              ref={articleBodyRef}
            >
              {blog.id === 79 && (
                <div
                  className={getClasses(
                    styles["blog__video"],
                    "rounded-lg embed-responsive embed-responsive-16by9",
                  )}
                >
                  <iframe
                    className="embed-responsive-item"
                    title="therapy-mission-statement"
                    src={"https://www.youtube.com/embed/wdfebgXoEkA"}
                    allowFullScreen
                  ></iframe>
                </div>
              )}
              <div dangerouslySetInnerHTML={{ __html: enhancedContent }} />
            </div>
            <AuthorFullBio
              blog={blog}
              orchidPro={orchidPro}
              amplitudePageName={AMPLITUDE_PAGE_NAME}
            />
            {isPhonePortrait && (
              <ShareThisArticle
                blog={blog}
                amplitudePageName={AMPLITUDE_PAGE_NAME}
              />
            )}
          </article>
          {!isPhonePortrait && (
            <div className={styles["blog__content__side-column"]}>
              <aside className={styles["blog__content__sticky-column-content"]}>
                <BlogCTA amplitudePageName={AMPLITUDE_PAGE_NAME} />
                <ShareThisArticle
                  blog={blog}
                  amplitudePageName={AMPLITUDE_PAGE_NAME}
                />
              </aside>
            </div>
          )}
        </Container>
      </div>
      <RelatedPosts
        blog={blog}
        category="Resources"
        amplitudePageName={AMPLITUDE_PAGE_NAME}
      />
      {isPhonePortrait && <BlogCTA amplitudePageName={AMPLITUDE_PAGE_NAME} />}
      <WriteForOrchid amplitudePageName={AMPLITUDE_PAGE_NAME} />
      <SubscribeToNewsletter
        setError={setError}
        AMPLITUDE_PAGE_NAME={AMPLITUDE_PAGE_NAME}
      />
      <Footer AMPLITUDE_PAGE_NAME={AMPLITUDE_PAGE_NAME} />
      {/* {showPopup && (
        <SubscribePopup show={true} onHide={() => setShowPopup(false)} />
      )} */}
    </>
  );
};

export default Blog;
